/**
 * MicrostructurePanel — Orderbook health metrics for the selected game.
 *
 * Displays a compact two-column (Kalshi | Poly) layout of spread, mid,
 * bid/ask depth, imbalance, and liquidity classification for the away side.
 *
 * @upstream  microstructure.ts (computeBookMetrics, formatSpread, formatImbalance)
 * @upstream  LiquidityBar atom
 */

import { useMemo } from 'react';
import { computeBookMetrics, formatSpread, formatImbalance } from '@/lib/pricing/microstructure';
import type { BookMetrics } from '@/lib/pricing/microstructure';
import { LiquidityBar } from '@/components/atoms/LiquidityBar';
import type { GameRow, VenueBook } from '@/views/ValueDashboard/types';
import { cn } from '@/lib/infra/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MicrostructurePanelProps {
  selectedGame: GameRow | null;
}

// ---------------------------------------------------------------------------
// Liquidity badge color mapping
// ---------------------------------------------------------------------------

const LIQUIDITY_COLORS: Record<BookMetrics['liquidityLabel'], string> = {
  HIGH: 'bg-green-500/20 text-green-400',
  MEDIUM: 'bg-yellow-500/20 text-yellow-400',
  LOW: 'bg-orange-500/20 text-orange-400',
  EMPTY: 'bg-red-500/20 text-red-400',
};

// ---------------------------------------------------------------------------
// Max depth for LiquidityBar scaling (dollars). Tuned to typical binary
// market depth so the bars aren't always pegged at 0% or 100%.
// ---------------------------------------------------------------------------

const DEPTH_MAX_DOLLARS = 5_000;

// ---------------------------------------------------------------------------
// VenueColumn — one column of metrics for a single venue
// ---------------------------------------------------------------------------

interface VenueColumnProps {
  label: string;
  badgeColor: string;
  metrics: BookMetrics | null;
}

function VenueColumn({ label, badgeColor, metrics }: VenueColumnProps) {
  if (!metrics) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center">
        <span className={cn('mb-1 rounded px-1.5 py-0.5 text-[10px] font-bold', badgeColor)}>
          {label}
        </span>
        <span className="text-muted-foreground/50 text-[10px]">No data</span>
      </div>
    );
  }

  const imbalanceAbs = Math.abs(metrics.imbalance);
  const imbalancePositive = metrics.imbalance >= 0;

  return (
    <div className="flex flex-1 flex-col gap-1.5 overflow-hidden px-2 py-1.5">
      {/* Venue badge */}
      <div className="flex items-center gap-1.5">
        <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-bold', badgeColor)}>
          {label}
        </span>
        <span
          className={cn(
            'rounded px-1.5 py-0.5 text-[10px] font-semibold',
            LIQUIDITY_COLORS[metrics.liquidityLabel],
          )}
        >
          {metrics.liquidityLabel}
        </span>
      </div>

      {/* Spread + Mid */}
      <div className="grid grid-cols-2 gap-x-2 text-[10px]">
        <div>
          <span className="text-muted-foreground">Spread </span>
          <span className="text-foreground font-medium">{formatSpread(metrics.spreadCents)}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Mid </span>
          <span className="text-foreground font-medium">
            {metrics.midPriceCents !== null ? `${metrics.midPriceCents.toFixed(1)}c` : '\u2014'}
          </span>
        </div>
      </div>

      {/* Bid depth */}
      <div className="text-[10px]">
        <div className="text-muted-foreground mb-0.5 flex items-center justify-between">
          <span>Bid Depth</span>
          <span className="text-foreground/70">
            {metrics.bidDepthContracts} cts &middot; ${metrics.bidDepthDollars.toFixed(0)}
          </span>
        </div>
        <LiquidityBar
          value={metrics.bidDepthDollars}
          maxValue={DEPTH_MAX_DOLLARS}
          height={12}
          showLabel={false}
        />
      </div>

      {/* Ask depth */}
      <div className="text-[10px]">
        <div className="text-muted-foreground mb-0.5 flex items-center justify-between">
          <span>Ask Depth</span>
          <span className="text-foreground/70">
            {metrics.askDepthContracts} cts &middot; ${metrics.askDepthDollars.toFixed(0)}
          </span>
        </div>
        <LiquidityBar
          value={metrics.askDepthDollars}
          maxValue={DEPTH_MAX_DOLLARS}
          height={12}
          showLabel={false}
        />
      </div>

      {/* Imbalance bar */}
      <div className="text-[10px]">
        <div className="text-muted-foreground mb-0.5 flex items-center justify-between">
          <span>Imbalance</span>
          <span className="text-foreground/70">{formatImbalance(metrics.imbalance)}</span>
        </div>
        <div className="bg-muted/50 relative h-2.5 w-full overflow-hidden rounded">
          <div
            className={cn(
              'absolute top-0 h-full rounded transition-all duration-300',
              imbalancePositive ? 'bg-green-500/70' : 'bg-red-500/70',
            )}
            style={{
              width: `${(imbalanceAbs * 100).toFixed(1)}%`,
              left: imbalancePositive ? '50%' : undefined,
              right: imbalancePositive ? undefined : '50%',
            }}
          />
          {/* Center marker */}
          <div className="bg-border absolute left-1/2 top-0 h-full w-px" />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MicrostructurePanel
// ---------------------------------------------------------------------------

export function MicrostructurePanel({ selectedGame }: MicrostructurePanelProps) {
  // --- Extract away books (optional-chained so the hooks below run
  //     unconditionally — Rules of Hooks: same call order every render, so the
  //     memos must precede the empty-state early return). ---
  const kalshiBook: VenueBook | undefined = selectedGame?.kalshi?.away;
  const polyBook: VenueBook | undefined = selectedGame?.polymarket?.away;

  // --- Compute metrics (memoised on book references) ---
  const kalshiMetrics = useMemo<BookMetrics | null>(() => {
    if (!kalshiBook) return null;
    return computeBookMetrics(kalshiBook.yes, kalshiBook.no);
  }, [kalshiBook]);

  const polyMetrics = useMemo<BookMetrics | null>(() => {
    if (!polyBook) return null;
    return computeBookMetrics(polyBook.yes, polyBook.no);
  }, [polyBook]);

  // --- Empty state (after the hooks so hook order stays stable) ---
  if (!selectedGame) {
    return (
      <div className="text-muted-foreground/50 flex h-full items-center justify-center text-xs">
        Select a game from Price Matrix
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Game label */}
      <div className="border-border/50 flex h-7 shrink-0 items-center border-b px-3">
        <span className="text-foreground text-[10px] font-semibold">
          {selectedGame.awayName || selectedGame.awayCode} @{' '}
          {selectedGame.homeName || selectedGame.homeCode}
        </span>
        <span className="text-muted-foreground ml-2 text-[10px]">away side</span>
      </div>

      {/* Two-column venue layout */}
      <div className="grid flex-1 grid-cols-2 gap-px overflow-y-auto bg-border">
        <div className="bg-card">
          <VenueColumn
            label="K"
            badgeColor="bg-blue-500/20 text-blue-400"
            metrics={kalshiMetrics}
          />
        </div>
        <div className="bg-card">
          <VenueColumn
            label="P"
            badgeColor="bg-purple-500/20 text-purple-400"
            metrics={polyMetrics}
          />
        </div>
      </div>
    </div>
  );
}
